import {
  Alert,
  Button,
  Flex,
  Modal,
  Radio,
  Table,
  Tag,
  Typography,
  notification
} from "antd"
import type { ColumnsType } from "antd/es/table"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { TextArea } from "../../../../components/Input/TextArea.tsx"
import { notificationOptions } from "../../../../constants/notification.config.ts"
import { empty } from "../../../../constants/placeholders.ts"
import { firstFieldError } from "../../../../utils/handlers/get-response-message.ts"
import { showResponseMessage } from "../../../../utils/handlers/show-response-message.ts"
import { budgetFormat } from "../../../../utils/number/budget-format.ts"
import {
  MAX_PASTE_ROWS,
  type MaterialPasteResult,
  parseMaterialPaste,
  pastedPlainText
} from "../../../../utils/material-paste/parse-material-paste.ts"
import * as services from "../../api/services.ts"
import type { MaterialDuplicateStrategy } from "../../api/services.types.ts"
import { listEverySupplierMaterial } from "../../helpers/list-every-material.ts"
import type { SupplierMaterial } from "../../types/types.ts"

const { Text } = Typography

type PreviewOutcome = "new" | "update" | "skip" | "error"

interface PreviewRow {
  key: string
  line: number
  name: string
  unit: string | null
  unitCost: string | null
  unitSalePrice: string | null
  outcome: PreviewOutcome
  /** The price the existing row holds, for an "updates — was …" note. */
  previousSalePrice?: string
  /** A parse reason, or what the server said about this row. */
  message?: string
}

interface MaterialPasteModalProps {
  supplierId: string
  supplierName: string
  onClose: () => void
  onImported: () => void
}

export const MaterialPasteModal = ({
  supplierId,
  supplierName,
  onClose,
  onImported
}: MaterialPasteModalProps) => {
  const { t } = useTranslation()

  const [text, setText] = useState("")
  const [result, setResult] = useState<MaterialPasteResult | null>(null)
  const [onDuplicate, setOnDuplicate] =
    useState<MaterialDuplicateStrategy>("update")
  const [existing, setExisting] = useState<Map<string, SupplierMaterial>>(
    new Map()
  )
  const [existingFailed, setExistingFailed] = useState(false)
  const [importing, setImporting] = useState(false)
  /** Keyed by the paste's own line number, so it survives a re-parse. */
  const [serverErrors, setServerErrors] = useState<Map<number, string>>(
    new Map()
  )

  useEffect(() => {
    let cancelled = false
    listEverySupplierMaterial(supplierId).then((response) => {
      if (cancelled) return
      if (!response.ok || !response.data) {
        setExistingFailed(true)
        return
      }
      setExisting(
        new Map(
          response.data.results.map((material) => [
            material.name.toLowerCase(),
            material
          ])
        )
      )
    })
    return () => {
      cancelled = true
    }
  }, [supplierId])

  const preview = useCallback((pasted: string) => {
    setText(pasted)
    setServerErrors(new Map())
    setResult(parseMaterialPaste(pasted))
  }, [])

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    // Excel puts a styled `text/html` flavour on the clipboard beside the TSV.
    // Only the TSV has a stable shape, so the default paste is replaced here
    // rather than trusted to pick the right one.
    event.preventDefault()
    preview(pastedPlainText(event.clipboardData))
  }

  const rows = useMemo<PreviewRow[]>(() => {
    if (!result) return []

    const accepted: PreviewRow[] = result.rows.map((row) => {
      const match = existing.get(row.name.toLowerCase())
      const outcome: PreviewOutcome = !match
        ? "new"
        : onDuplicate === "skip"
          ? "skip"
          : "update"

      return {
        key: `row-${row.line}`,
        line: row.line,
        name: row.name,
        unit: row.unit,
        unitCost: row.unit_cost,
        unitSalePrice: row.unit_sale_price,
        outcome,
        previousSalePrice: match?.unit_sale_price,
        message: serverErrors.get(row.line)
      }
    })

    const failed: PreviewRow[] = result.errors.map((error) => ({
      key: `error-${error.line}`,
      line: error.line,
      name: error.name.trim(),
      unit: error.unit,
      unitCost: error.unitCost,
      unitSalePrice: error.unitSalePrice,
      outcome: "error",
      message: t(`supplierMaterials.paste.errors.${error.reason}`)
    }))

    return [...accepted, ...failed].sort((a, b) => a.line - b.line)
  }, [result, existing, onDuplicate, serverErrors, t])

  const counts = useMemo(() => {
    const newCount = rows.filter((row) => row.outcome === "new").length
    const updateCount = rows.filter((row) => row.outcome === "update").length
    const skippedCount = rows.filter(
      (row) => row.outcome === "skip" || row.outcome === "error"
    ).length
    return { newCount, updateCount, skippedCount }
  }, [rows])

  const importable = counts.newCount + counts.updateCount

  const handleImport = async () => {
    if (!result?.rows.length) return

    setImporting(true)
    try {
      const response = await services.bulkUpsertSupplierMaterials({
        supplier: supplierId,
        items: result.rows.map(({ name, unit, unit_cost, unit_sale_price }) => ({
          name,
          unit,
          unit_cost,
          unit_sale_price
        })),
        on_duplicate: onDuplicate
      })

      if (response.ok) {
        const summary = response.data?.details?.data
        // Not the server's own message: the envelope renderer conjugates the
        // action name, so this one arrives as "Supplier Material bulked
        // successfully". The figures it returns are what the user wants.
        notification.success({
          ...notificationOptions.success,
          message: t("supplierMaterials.paste.imported", {
            created: summary?.created ?? 0,
            updated: summary?.updated ?? 0,
            skipped: summary?.skipped ?? 0
          })
        })
        onImported()
        return
      }

      // Nothing was written — the endpoint applies a paste whole or not at
      // all — so the preview stays open with the refusal shown per row.
      // The platform's error handler rewrites every 4xx body and folds the
      // serializer's own keys under `details`, so the per-row array never
      // arrives at the top level — read there, the whole refused-rows UI is
      // unreachable and a 60-row paste reports one unplaceable "Validation
      // error".
      const perRow = response.data?.details?.items ?? response.data?.items
      if (Array.isArray(perRow)) {
        const mapped = new Map<number, string>()
        perRow.forEach((fieldErrors, index) => {
          const line = result.rows[index]?.line
          const message = firstFieldError(fieldErrors)
          if (line !== undefined && message) mapped.set(line, message)
        })
        setServerErrors(mapped)
        if (mapped.size) return
      }
      showResponseMessage(response, t("supplierMaterials.paste.importFailed"))
    } finally {
      setImporting(false)
    }
  }

  const columns = useMemo<ColumnsType<PreviewRow>>(
    () => [
      {
        title: t("supplierMaterials.columns.name"),
        dataIndex: "name",
        key: "name",
        ellipsis: true,
        render: (value: string) => value || empty
      },
      {
        title: t("supplierMaterials.columns.unit"),
        dataIndex: "unit",
        key: "unit",
        width: 80,
        render: (value: string | null) => value || empty
      },
      {
        title: t("supplierMaterials.columns.unitCost"),
        dataIndex: "unitCost",
        key: "unitCost",
        width: 100,
        align: "right",
        render: (value: string | null) =>
          value === null ? empty : budgetFormat(value, { minimumFractionDigits: 2 })
      },
      {
        title: t("supplierMaterials.columns.unitSalePrice"),
        dataIndex: "unitSalePrice",
        key: "unitSalePrice",
        width: 110,
        align: "right",
        // An unreadable cell is shown exactly as it was pasted, so the user
        // can see which character is the problem.
        render: (value: string | null, record) =>
          record.outcome === "error"
            ? (value ?? empty)
            : budgetFormat(value, { minimumFractionDigits: 2 })
      },
      {
        title: t("supplierMaterials.paste.statusColumn"),
        key: "status",
        width: 210,
        render: (_value: unknown, record: PreviewRow) => {
          if (record.outcome === "error" || record.message) {
            return <Text type="danger">{record.message}</Text>
          }
          if (record.outcome === "new") {
            return <Text type="success">{t("supplierMaterials.paste.new")}</Text>
          }
          if (record.outcome === "skip") {
            return (
              <Text type="secondary">
                {t("supplierMaterials.paste.skippedExisting")}
              </Text>
            )
          }
          return (
            <Text className="text-blue-600">
              {t("supplierMaterials.paste.updates", {
                previous: budgetFormat(record.previousSalePrice ?? null, {
                  minimumFractionDigits: 2
                })
              })}
            </Text>
          )
        }
      }
    ],
    [t]
  )

  const blocked = useMemo(() => {
    if (!result?.blocked) return null
    const { reason, headings } = result.blocked
    return t(
      `supplierMaterials.paste.blocked.${reason}`,
      headings.length ? { headings: headings.join(", ") } : undefined
    )
  }, [result, t])

  const notices = [
    result?.headerSkipped ? t("supplierMaterials.paste.headerSkipped") : null,
    result?.twoColumnRows ? t("supplierMaterials.paste.twoColumns") : null,
    result?.extraColumnsIgnored
      ? t("supplierMaterials.paste.extraColumns")
      : null,
    result?.duplicateCount
      ? t("supplierMaterials.paste.duplicates", { count: result.duplicateCount })
      : null,
    result?.droppedForCap
      ? t("supplierMaterials.paste.capped", {
          max: MAX_PASTE_ROWS,
          count: result.droppedForCap
        })
      : null,
    existingFailed ? t("supplierMaterials.paste.existingUnknown") : null
  ].filter(Boolean) as string[]

  return (
    <Modal
      open
      centered
      width={860}
      maskClosable={false}
      title={
        <Flex align="baseline" gap={10}>
          <span>{t("supplierMaterials.paste.title")}</span>
          <Text type="secondary" className="text-sm font-normal">
            {supplierName}
          </Text>
        </Flex>
      }
      onCancel={onClose}
      footer={
        <Flex align="center" gap={12}>
          <Text type="secondary" className="text-xs">
            {t("supplierMaterials.paste.nothingSavedYet")}
          </Text>
          <div className="grow" />
          {/* Away from the duplicate-strategy group, where it read as a third
              strategy rather than a way back to the textarea. */}
          {!!result && (
            <Button onClick={() => setResult(null)}>
              {t("supplierMaterials.paste.pasteAgain")}
            </Button>
          )}
          <Button onClick={onClose}>{t("suppliers.cancel")}</Button>
          <Button
            type="primary"
            loading={importing}
            disabled={!importable}
            onClick={handleImport}
          >
            {t("supplierMaterials.paste.import", { count: importable })}
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap={14}>
        {!result ? (
          <>
            <Text type="secondary">
              {t("supplierMaterials.paste.instructions")}
            </Text>
            <TextArea
              disableAutoCapitalize
              autoFocus
              rows={8}
              value={text}
              onPaste={handlePaste}
              onChange={(event) => setText(event.target.value)}
              placeholder={t("supplierMaterials.paste.placeholder")}
              aria-label={t("supplierMaterials.paste.textareaLabel")}
            />
            <Flex justify="flex-end">
              <Button disabled={!text.trim()} onClick={() => preview(text)}>
                {t("supplierMaterials.paste.preview")}
              </Button>
            </Flex>
          </>
        ) : (
          <>
            <Flex align="center" gap={10} wrap>
              <Tag color="green">
                {t("supplierMaterials.paste.newCount", {
                  count: counts.newCount
                })}
              </Tag>
              <Tag color="blue">
                {t("supplierMaterials.paste.updateCount", {
                  count: counts.updateCount
                })}
              </Tag>
              <Tag color="red">
                {t("supplierMaterials.paste.skippedCount", {
                  count: counts.skippedCount
                })}
              </Tag>
              <div className="grow" />
              <Text type="secondary" className="text-xs">
                {t("supplierMaterials.paste.alreadyInTheList")}
              </Text>
              <Radio.Group
                size="small"
                value={onDuplicate}
                onChange={(event) => setOnDuplicate(event.target.value)}
                options={[
                  {
                    label: t("supplierMaterials.paste.updatePrices"),
                    value: "update"
                  },
                  { label: t("supplierMaterials.paste.skip"), value: "skip" }
                ]}
                optionType="button"
              />
            </Flex>

            {!!blocked && <Alert type="warning" showIcon message={blocked} />}

            {notices.map((notice) => (
              <Alert key={notice} type="info" showIcon message={notice} />
            ))}

            {!!serverErrors.size && (
              <Alert
                type="error"
                showIcon
                message={t("supplierMaterials.paste.refused")}
              />
            )}

            <Table<PreviewRow>
              size="small"
              rowKey="key"
              columns={columns}
              dataSource={rows}
              pagination={false}
              scroll={{ y: 320, x: "max-content" }}
              rowClassName={(record) =>
                record.outcome === "error" ? "bg-red-50" : ""
              }
              locale={{ emptyText: t("supplierMaterials.paste.nothingToImport") }}
            />
          </>
        )}
      </Flex>
    </Modal>
  )
}
